import React, { type Ref, useCallback, useState } from 'react';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { fg } from '@atlassian/jira-feature-gating';
import { useTrigger } from '@atlassian/post-office-context';
import {
	MINIMUM_COLUMN_WIDTH,
	SINGLE_LINE_ROW_HEIGHT_MINIMUM_COLUMN_WIDTH,
	MINIMUM_MERGED_CELL_COLUMN_WIDTH,
	SORT_ASC,
	SORT_DESC,
	SORT_NONE,
} from '../../../../../common/constants.tsx';
import type { Column, TargetColumnPosition } from '../../../../../common/types.tsx';
import {
	useIsFixedTableLayoutEnabled,
	useIsSingleLineRowHeightEnabled,
	useIsStickyColumnsEnabled,
} from '../../../../../controllers/features/selectors.tsx';
import { MoreActions } from './more-actions/index.tsx';
import { SortButton } from './sort-button/index.tsx';

export type Props = {
	column: Column;
	previousColumn?: Column;
	currentColumnWidth: number;
	hasColumnConfiguration: boolean;
	isColumnResizingEnabled: boolean;
	isCompactColumnEnabled?: boolean;
	isHeaderCellHovered: boolean;
	isFirst: boolean;
	isLast: boolean;
	isOpen: boolean;
	triggerRef?: Ref<HTMLButtonElement>;
	isMergedColumn?: boolean;
	enableDynamicOrderForMergedColumn?: boolean;
	onMoveColumn: (position: TargetColumnPosition) => void;
	onOpenChange: (isOpen: boolean) => void;
	onRemoveColumn: () => void;
	onResizeColumn: () => void;
	onFreezeColumn?: () => void;
	onUnfreezeColumn?: () => void;
	onResetColumnWidth: () => void;
	// TODO: Make this required when refactor_issue_table_for_timline is being cleaned up
	mergedColumnMinWidth?: number;
	totalMoveableColumns?: number;
};

export const HeaderCellActions = ({
	column,
	previousColumn,
	currentColumnWidth,
	hasColumnConfiguration,
	isColumnResizingEnabled,
	isCompactColumnEnabled = false,
	isHeaderCellHovered,
	isFirst,
	isLast,
	isOpen,
	triggerRef = null,
	isMergedColumn = false,
	enableDynamicOrderForMergedColumn = false,
	onMoveColumn,
	onOpenChange,
	onRemoveColumn,
	onResizeColumn,
	onFreezeColumn,
	onUnfreezeColumn,
	onResetColumnWidth,
	mergedColumnMinWidth,
	totalMoveableColumns,
}: Props) => {
	const [isFocused, setIsFocused] = useState(false);

	const onFocus = useCallback(() => {
		setIsFocused(true);
	}, []);

	const onBlur = useCallback(() => {
		setIsFocused(false);
	}, []);

	const isSortable = column.onSortOrderChanged !== undefined && column.isSortable;

	const isFixedTableLayoutEnabled = useIsFixedTableLayoutEnabled();
	// Header actions width must be static when fixed table layout is disabled to prevent column
	// widths shifting as the user hovers the table header
	const isStaticWidth = !isFixedTableLayoutEnabled;

	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();
	const isStickyColumnsEnabled = useIsStickyColumnsEnabled();

	const { isActive: isSpotlightVisible } = fg('frozen_columns_onboarding')
		? // eslint-disable-next-line  react-hooks/rules-of-hooks
			useTrigger(
				isMergedColumn && isStickyColumnsEnabled
					? 'frozen-columns-onboarding-is-spotlight-visible'
					: undefined,
			)
		: { isActive: false };

	let minimumColumnWidth;
	if (isMergedColumn && column.mergedColumns && column.mergedColumns.length > 1) {
		minimumColumnWidth = mergedColumnMinWidth || MINIMUM_MERGED_CELL_COLUMN_WIDTH;
	} else {
		minimumColumnWidth = isSingleLineRowHeightEnabled
			? SINGLE_LINE_ROW_HEIGHT_MINIMUM_COLUMN_WIDTH
			: MINIMUM_COLUMN_WIDTH;
	}

	if (!isSortable && !hasColumnConfiguration && !isColumnResizingEnabled) {
		return null;
	}

	const isMoreActionsVisible = isHeaderCellHovered || isOpen || isFocused || isSpotlightVisible;

	const isSorted = column.currentSorting === SORT_ASC || column.currentSorting === SORT_DESC;
	const isSortVisible = isMoreActionsVisible || isSorted;

	const isSortOnlyAction = isSortable && !hasColumnConfiguration && !isColumnResizingEnabled;

	const sortButton = (
		<Box xcss={[baseStyles, !isSortVisible && hiddenStyles]}>
			<SortButton column={column} onFocus={onFocus} onBlur={onBlur} />
		</Box>
	);

	const moreActionsButton = (
		<Box xcss={[baseStyles, !isMoreActionsVisible && hiddenStyles]}>
			<MoreActions
				column={column}
				previousColumn={previousColumn}
				currentColumnWidth={currentColumnWidth}
				hasColumnConfiguration={hasColumnConfiguration}
				isColumnResizingEnabled={isColumnResizingEnabled}
				isFirst={isFirst}
				isLast={isLast}
				isOpen={isOpen}
				triggerRef={triggerRef}
				isMergedColumn={isMergedColumn}
				enableDynamicOrderForMergedColumn={enableDynamicOrderForMergedColumn}
				onMoveColumn={onMoveColumn}
				onOpenChange={onOpenChange}
				onRemoveColumn={onRemoveColumn}
				onResizeColumn={onResizeColumn}
				onFreezeColumn={onFreezeColumn}
				onUnfreezeColumn={onUnfreezeColumn}
				onResetColumnWidth={onResetColumnWidth}
				onFocus={onFocus}
				onBlur={onBlur}
				{...(fg('jira_timeline_sticky_assignee_status_columns') && {
					totalMoveableColumns,
				})}
			/>
		</Box>
	);

	if (isSortOnlyAction) {
		return (
			<Inline xcss={[containerStyles, isStaticWidth && singleButtonWidth]}>{sortButton}</Inline>
		);
	}

	if (!isSortable) {
		return (
			<Inline xcss={[containerStyles, isStaticWidth && singleButtonWidth]}>
				{moreActionsButton}
			</Inline>
		);
	}

	if (isCompactColumnEnabled && currentColumnWidth < minimumColumnWidth) {
		if (column.currentSorting !== SORT_NONE && !isHeaderCellHovered) {
			return (
				<Inline xcss={[containerStyles, isStaticWidth && singleButtonWidth]}>{sortButton}</Inline>
			);
		}
		return (
			<Inline xcss={[containerStyles, isStaticWidth && singleButtonWidth]}>
				{moreActionsButton}
			</Inline>
		);
	}

	return (
		<Inline
			alignInline={isStaticWidth ? 'end' : undefined}
			xcss={[
				containerStyles,
				// Animate between single and all button width when sorted (only when in a dynamic layout)
				!isStaticWidth && isSorted && singleButtonWidth,
				(isStaticWidth || isMoreActionsVisible) && allButtonsWidth,
			]}
		>
			{sortButton}
			{moreActionsButton}
		</Inline>
	);
};

const containerStyles = xcss({
	gap: 'space.050',
	transition: 'width .25s',
});

const singleButtonWidth = xcss({
	width: '24px',
});

const allButtonsWidth = xcss({
	width: '52px', // 2 x 24px icon + 4px gap
});

const baseStyles = xcss({
	transition: 'opacity .25s',
});

const hiddenStyles = xcss({
	clip: 'rect(1px, 1px, 1px, 1px)',
	clipPath: 'inset(50%)',
	height: '1px',
	width: '1px',
	margin: 'space.negative.025',
	overflow: 'hidden',
	padding: 'space.0',
	opacity: 0,
	position: 'absolute',
	pointerEvents: 'none', // ensure hidden controls cannot be the target of any mouse events, e.g. hovering
});
